import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ContactHero from './components/ContactHero';
import ContactOfficeDetails from './components/ContactOfficeDetails';
import ContactMap from './components/ContactMap';
import ContactForm from './components/ContactForm';
import ContactFAQ from './components/ContactFAQ';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SeoHead from '../../components/SeoHead';
import { RefObject } from 'react';

const contactJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact CJ Solutions Ltd',
    url: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}/contact`,
    description: 'Get in touch with CJ Solutions Ltd in Limassol, Cyprus. Book a free consultation for compliance, AML, licensing, and financial advisory services.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How quickly do you respond to enquiries?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We aim to respond to all enquiries within 24 business hours.',
        },
      },
      {
        '@type': 'Question',
        name: 'Where is CJ Solutions Ltd located?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'CJ Solutions Ltd is based in Limassol, Cyprus.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do you offer a free initial consultation?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we offer a free, no-obligation initial consultation for all prospective clients.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}` },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}/contact` },
    ],
  },
];

function OfficeSection() {
  const { ref: detailsRef, isVisible: detailsVisible } = useScrollAnimation();
  const { ref: mapRef, isVisible: mapVisible } = useScrollAnimation();

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          <div
            ref={detailsRef as RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${detailsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <ContactOfficeDetails />
          </div>
          <div
            ref={mapRef as RefObject<HTMLDivElement>}
            className={`transition-all duration-700 delay-150 ${mapVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <ContactMap />
          </div>
        </div>
      </div>
    </section>
  );
}

function FormSection() {
  const { ref: copyRef, isVisible: copyVisible } = useScrollAnimation();
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation();

  const bullets = [
    'Response within 24 hours',
    'Confidential & no-obligation',
    'Specialists across 9+ service areas',
    'Based in Limassol, Cyprus',
  ];

  return (
    <section className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div
            ref={copyRef as RefObject<HTMLDivElement>}
            className={`lg:col-span-4 space-y-5 lg:sticky lg:top-32 transition-all duration-700 ${copyVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <span className="inline-block px-3 py-1 bg-white border border-slate-200 text-slate-500 text-xs font-semibold uppercase tracking-widest rounded">
              Let&apos;s Build Your Success Together
            </span>
            <h2 className="text-4xl sm:text-5xl font-semibold text-slate-900 leading-tight">
              Ready to<br />Take the<br />
              <span className="text-amber-500">Next Step?</span>
            </h2>
            <p className="text-slate-600 text-base leading-relaxed">
              Whether you&apos;re launching, expanding, or streamlining your operations, our experts are here to guide you with tailored solutions.
            </p>
            <ul className="space-y-3">
              {bullets.map((item) => (
                <li key={item} className="flex items-center gap-3 text-slate-700 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-amber-500 text-lg"></i>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div
            ref={formRef as RefObject<HTMLDivElement>}
            className={`lg:col-span-8 transition-all duration-700 delay-150 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Contact CJ Solutions | Free Consultation Cyprus"
        description="Contact CJ Solutions Ltd in Limassol, Cyprus. Book a free, no-obligation consultation for compliance, AML, iGaming licensing, and financial advisory services."
        keywords="contact CJ Solutions, compliance consultation Cyprus, Limassol consulting, free consultation AML licensing"
        canonical="/contact"
        jsonLd={contactJsonLd}
      />
      <Header />
      <main>
        <ContactHero />
        <div style={{contentVisibility: 'auto', containIntrinsicSize: '0 600px'}}>
          <OfficeSection />
        </div>
        <div style={{contentVisibility: 'auto', containIntrinsicSize: '0 700px'}}>
          <FormSection />
        </div>
        <div style={{contentVisibility: 'auto', containIntrinsicSize: '0 500px'}}>
          <ContactFAQ />
        </div>
      </main>
      <Footer />
    </div>
  );
}
